import type {
  AttentionFilter,
  Conversation,
  ConversationFilter,
  ConversationStateFilter,
  IntentFilter,
} from './conversations-model'
import type {
  ActionRequestType,
  InteractionOperationalState,
} from './interaction-operational-contracts'
import { conversationAttention } from './conversation-attention'

export type ConversationFilterSelection = {
  query: string
  channel: ConversationFilter
  attention: AttentionFilter
  intent: IntentFilter
  state: ConversationStateFilter
}

const intentRequestType: Partial<Record<IntentFilter, ActionRequestType>> = {
  'Appointment requests': 'APPOINTMENT_REQUEST',
  'Callback requests': 'CALLBACK_REQUEST',
  'Cancellation requests': 'CANCELLATION_REQUEST',
  'Reschedule requests': 'RESCHEDULE_REQUEST',
}

const requestTypeQueryValue: Record<ActionRequestType, string> = {
  APPOINTMENT_REQUEST: 'appointment',
  CALLBACK_REQUEST: 'callback',
  CANCELLATION_REQUEST: 'cancellation',
  RESCHEDULE_REQUEST: 'reschedule',
}

const queryValueIntentFilter: Record<string, IntentFilter> = {
  appointment: 'Appointment requests',
  callback: 'Callback requests',
  cancellation: 'Cancellation requests',
  reschedule: 'Reschedule requests',
}

export function intentFilterFromRequestTypeQuery(
  value: string | null
): IntentFilter {
  return value
    ? (queryValueIntentFilter[value] ?? 'Any open request')
    : 'Any open request'
}

export function requestTypeQueryFromIntentFilter(
  filter: IntentFilter
): string | null {
  const requestType = intentRequestType[filter]
  return requestType ? requestTypeQueryValue[requestType] : null
}

export function conversationsPathForRequestType(
  requestType: ActionRequestType,
  aggregateId?: string | null
) {
  const params = new URLSearchParams({
    requestType: requestTypeQueryValue[requestType],
  })
  if (aggregateId) params.set('aggregate', aggregateId)
  return `/conversations?${params.toString()}`
}

export function matchesConversationFilters(
  conversation: Conversation,
  operational: InteractionOperationalState | undefined,
  filters: ConversationFilterSelection
) {
  if (
    filters.channel !== 'All' &&
    !conversation.availableChannels.includes(filters.channel)
  ) {
    return false
  }
  const attention = conversationAttention(operational)
  if (filters.attention === 'Needs Action' && attention !== 'NEEDS_ACTION') {
    return false
  }
  // Mutually exclusive with Needs Action: an open request is never review.
  if (filters.attention === 'Needs Review' && attention !== 'NEEDS_REVIEW') {
    return false
  }
  if (filters.attention === 'Unread' && !operational?.unreadForCaller) {
    return false
  }
  if (filters.attention === 'Starred' && !operational?.starred) return false
  if (
    filters.state === 'Reviewed' &&
    (attention !== 'NONE' || operational?.reviewStatus !== 'REVIEWED')
  ) {
    return false
  }
  const requestType = intentRequestType[filters.intent]
  if (
    requestType &&
    !operational?.unresolvedActionRequestTypes.includes(requestType)
  ) {
    return false
  }
  const query = filters.query.trim().toLocaleLowerCase()
  if (!query) return true
  const phoneQuery = query.replace(/\D/g, '')
  return (
    conversation.details.displayName.toLocaleLowerCase().includes(query) ||
    conversation.details.associatedPatientNames?.some((name) =>
      name.toLocaleLowerCase().includes(query)
    ) === true ||
    (phoneQuery.length > 0 &&
      (conversation.details.phone ?? '')
        .replace(/\D/g, '')
        .includes(phoneQuery))
  )
}
